/**
 * Buffer-day rule matching — resolves which configured IBufferDaysRule(s)
 * apply to a window item's answers, and the highest buffer-days value
 * across every window item in a quote/order.
 *
 * Matching: a rule's field only restricts matching when it's non-empty —
 * an empty/absent rule field means "no restriction on this field". All of
 * a rule's non-empty fields must be satisfied for the rule to match.
 *
 * `timberLockId` matches against the "Timber Look Joints" Yes/No question's
 * answer optionId (optionType 94).
 */

import { IAssistantMessage, IQuestionOption } from "@/features/assistant/types";
import { isProductOption } from "@/features/assistant/utils/product";
import {
  MESSAGE_TYPE_ENUM,
  PROFILE_COLOR_OPTION,
  OPTION_TYPE_SPRAY,
  OPTION_TYPE_SECURITY,
  OPTION_TYPE_TIMBER_LOOK_JOINTS,
} from "@/features/assistant/constants";
import { IBufferDaysRule } from "../types";

// "Internal Colour" / "External Colour" questions under the Profile Colours
// category — distinct from spray RAL colours (12/13) and hardware swatches.
const INTERNAL_COLOUR_OPTION_TYPE = 35;
const EXTERNAL_COLOUR_OPTION_TYPE = 34;

const findAnswerOptionId = (
  messages: IAssistantMessage[],
  optionType: number,
): string | null => {
  const msg = messages.find(
    (m) => m.type === MESSAGE_TYPE_ENUM.user && m.question?.optionType === optionType,
  );
  return msg?.response?.answer?.[0]?.optionId ?? null;
};

export interface IBufferDayMatchContext {
  region: number | null;
  productId: string | null;
  profileColourTypeId: string | null;
  internalColorId: string | null;
  externalColorId: string | null;
  sprayId: string | null;
  securityId: string | null;
  timberLockId: string | null;
}

/** Extracts the fields a buffer-day rule can match against from a single window item's answers. */
export const getBufferDayMatchContext = (
  messages: IAssistantMessage[],
  region: number | null,
): IBufferDayMatchContext => {
  let productId: string | null = null;
  for (const msg of messages) {
    if (msg.type !== MESSAGE_TYPE_ENUM.user) continue;
    const answers = Array.isArray(msg.response?.answer) ? msg.response.answer : [];
    const product = answers.find((a) => a && isProductOption(a as IQuestionOption));
    if (product) {
      productId = (product as IQuestionOption).optionId ?? null;
      break;
    }
  }

  return {
    region,
    productId,
    profileColourTypeId: findAnswerOptionId(messages, PROFILE_COLOR_OPTION),
    internalColorId: findAnswerOptionId(messages, INTERNAL_COLOUR_OPTION_TYPE),
    externalColorId: findAnswerOptionId(messages, EXTERNAL_COLOUR_OPTION_TYPE),
    sprayId: findAnswerOptionId(messages, OPTION_TYPE_SPRAY),
    securityId: findAnswerOptionId(messages, OPTION_TYPE_SECURITY),
    timberLockId: findAnswerOptionId(messages, OPTION_TYPE_TIMBER_LOOK_JOINTS),
  };
};

/** Which field caused a rule to not match (undefined = it matched). */
export const explainBufferDayRuleMismatch = (
  rule: IBufferDaysRule,
  context: IBufferDayMatchContext,
): string | undefined => {
  if (rule.deleted) return "deleted";

  if (rule.regions?.length && (context.region == null || !rule.regions.includes(context.region))) {
    return `region (rule wants one of [${rule.regions}], item has ${context.region})`;
  }

  if (rule.productIds?.length && (!context.productId || !rule.productIds.includes(context.productId))) {
    return `productId (rule wants one of [${rule.productIds}], item has ${context.productId})`;
  }

  if (rule.profileColourTypeId && rule.profileColourTypeId !== context.profileColourTypeId) {
    return `profileColourTypeId (rule wants ${rule.profileColourTypeId}, item has ${context.profileColourTypeId})`;
  }

  if (rule.sprayId && rule.sprayId !== context.sprayId) {
    return `sprayId (rule wants ${rule.sprayId}, item has ${context.sprayId})`;
  }

  if (rule.securityIds?.length && (!context.securityId || !rule.securityIds.includes(context.securityId))) {
    return `securityId (rule wants one of [${rule.securityIds}], item has ${context.securityId})`;
  }

  if (rule.timberLockId && rule.timberLockId !== context.timberLockId) {
    return `timberLockId (rule wants ${rule.timberLockId}, item has ${context.timberLockId})`;
  }

  if (rule.colourCombinations?.length) {
    const matchesCombo = rule.colourCombinations.some(
      (combo) =>
        combo.internalColorId === context.internalColorId &&
        combo.externalColorId === context.externalColorId,
    );
    if (!matchesCombo) {
      return `colourCombinations (rule wants one of ${JSON.stringify(rule.colourCombinations)}, item has {internal: ${context.internalColorId}, external: ${context.externalColorId}})`;
    }
  }

  return undefined;
};

/** Whether a buffer-day rule applies to a window item's extracted answers. */
export const doesBufferDayRuleMatchItem = (
  rule: IBufferDaysRule,
  context: IBufferDayMatchContext,
): boolean => explainBufferDayRuleMismatch(rule, context) === undefined;

/**
 * Resolves a single buffer-days value for the whole order, per the resolution
 * spec:
 *   1. For every frame (window item), find all configured rules matching that
 *      frame's region/product/colour/spray/security/timber-lock answers.
 *   2. A frame matching multiple rules takes the HIGHEST of those rules'
 *      bufferDays (e.g. a foil-on-white arch that's also sprayed).
 *   3. A frame matching NO rule is NOT excluded — the minimum configured
 *      bufferDays (across all active rules) applies to it instead.
 *   4. The order's resolved value is the HIGHEST across all frames — one
 *      slow component governs the whole order's delivery date.
 * `defaultBufferDays` is only used when there are no active rules configured
 * at all (or no items), since step 3's "minimum configured" has nothing to
 * fall back to in that case.
 */
export const getHighestBufferDays = (
  itemsMessages: IAssistantMessage[][],
  rules: IBufferDaysRule[],
  region: number | null,
  defaultBufferDays = 7,
): number => {
  const activeRules = rules.filter((rule) => !rule.deleted);

  if (activeRules.length === 0 || itemsMessages.length === 0) {
    return defaultBufferDays;
  }

  const minConfiguredBufferDays = Math.min(...activeRules.map((rule) => rule.bufferDays));

  let orderHighest = -Infinity;

  itemsMessages.forEach((messages) => {
    const context = getBufferDayMatchContext(messages, region);

    let itemHighest = -Infinity;
    activeRules.forEach((rule) => {
      if (doesBufferDayRuleMatchItem(rule, context)) {
        itemHighest = Math.max(itemHighest, rule.bufferDays);
      }
    });

    if (itemHighest === -Infinity) {
      itemHighest = minConfiguredBufferDays;
    }

    orderHighest = Math.max(orderHighest, itemHighest);
  });

  return orderHighest;
};
